import React, { useEffect, useState, useMemo, useRef } from 'react'
import { useFormContext, Controller, FieldValues, Path, PathValue } from 'react-hook-form'
import { GetShipperListV2Req } from '@/services/shipper/types'
import MultipleSelectBoxOriginal from '@/components/content/MultipleSelectBox'
import SearchableMultipleSelectOriginal from '@/components/content/SearchableMultipleSelect'
import SelectBox from '@/components/content/SelectBox'
import styled from 'styled-components'
import { useGetShipperNames } from '@/services/shipper/useShipperQueries'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { SHIPPER_TYPES } from '@/services/shipper/apis'
import { OutboundWaitingUrlParams } from '@/services/outbound/outboundWaitingStatus/types'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { canSelectShipper, defaultShipper, selectableShippers, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { useCoMingleShipperScope } from '@/components/system/useCoMingleShipperScope'

const INIT_FORM = {
  logisticsServiceTypes: SHIPPER_TYPES,
}

const Container = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`

const MultipleSelectBox = styled(MultipleSelectBoxOriginal)<{ $width?: number }>`
  min-width: ${(props) => props.$width || 120}px;
  width: ${(props) => props.$width || 120}px;
`

const StyledSelectBox = styled(SelectBox)<{ $width?: number }>`
  min-width: ${(props) => props.$width || 120}px;
`

const StyledSearchableMultipleSelect = styled(SearchableMultipleSelectOriginal)<{ $width?: number }>`
  width: ${(props) => props.$width || 250}px;
`

/**
 * ShipperSelector 필드 변경 이벤트 파라미터
 *
 * 정책:
 * - isSelectedAll: true인 경우, 서버 전송 시 해당 필드를 null로 변환해야 함 (서버에서 null은 "전체 선택"을 의미 (필터링하지 않음))
 */
interface ShipperSelectorChangeParams<T extends FieldValues = any> {
  fieldName: keyof T
  value: unknown
  isSelectedAll?: boolean
}

interface ShipperSelectorProps<T extends FieldValues = GetShipperListV2Req> {
  onFieldChange?: (params: ShipperSelectorChangeParams<T>) => void
  logisticsServiceTypesWidth?: number
  shipperNamesWidth?: number
  disabled?: boolean
  isSingleLogisticsServiceType?: boolean
  /**
   * 화주명을 전체 선택만 가능하도록 해야하는 경우
   *
   * 예시: 출고대기현황 B2B화면에서 MFS 화주 유형 선택된 경우
   * 정책: 화주명을 전체 선택 상태로 초기화
   */
  onlyFullSearchShipperNamesMode?: boolean
  fieldNames?: {
    shipperNames: Path<T>
    logisticsServiceTypes: Path<T>
  }
}

/**
 * 화주명 데이터 가져오기 및 초기값 설정 훅
 *
 * 정책:
 * 1. 첫 번째 렌더링 시 URL 파라미터의 shipperNames로 초기화
 * 2. URL 파라미터에 없는 경우 사용자 기본값으로 초기화
 * 3. onlyFullSearchShipperNamesMode가 true인 경우 전체 선택으로 초기화
 * 4. 화주유형이 변경되면 화주명을 빈 배열로 리셋
 */
const useShipperNamesWithInit = ({
  logisticsServiceTypes,
  setValue,
  onlyFullSearchShipperNamesMode,
  isShipperLocked,
}: {
  logisticsServiceTypes: string[]
  setValue: any
  onlyFullSearchShipperNamesMode: boolean
  /** 잠긴 계정은 기본값을 여기서 넣지 않는다 — 잠금 effect 가 범위로 맞춘다. 넣으면 복원한 MUSINSA 가 자기 화주로 덮인다 (LOGISPRD-15651) */
  isShipperLocked: boolean
}) => {
  const { parsedQs } = useParsedQuery<OutboundWaitingUrlParams>()
  const isFirstRenderRef = useRef(true)

  const { data: shipperNames, isSuccess } = useGetShipperNames({
    logisticsServiceTypes,
  })

  /**
   * 특이사항: 선택된 화주유형으로 조회한 이후에 초기값 세팅(parsedQs 기반한 세팅)이 가능하기 때문에 useEffect 내에서 처리함.
   */
  useEffect(() => {
    if (isSuccess && shipperNames && shipperNames.length > 0) {
      // 정책: 전체 선택만 가능하도록 초기값 설정
      if (onlyFullSearchShipperNamesMode) {
        setValue('shipperNames', shipperNames)
        return
      }

      if (isFirstRenderRef.current) {
        // 첫 번째 렌더링 (메뉴 이동인 경우) - parsedQs의 값으로 초기화
        const initShipperNames = Array.isArray(parsedQs?.shipperNames)
          ? parsedQs?.shipperNames
          : parsedQs?.shipperNames
            ? [parsedQs?.shipperNames]
            : isShipperLocked
              ? []
              : // 기본 화주는 현재 화주의 이름(shipper_name, 없으면 코드) — 비교 대상 shipperNames 가 이름 목록이고
                // 잠금 분기(아래 effect)도 같은 값을 넣는다. SPR_NM 은 로그인 때 화주라 전환 뒤 틀리고,
                // 모듈 상수로 두면 전환 뒤 재마운트에도 낡은 값이 남아 첫 렌더에 읽는다 (LOGISPRD-14650)
                [shipperScopeOf(AuthStorage.user.get()).shipperName]

        const matchedNames = initShipperNames.filter((initShipperName) => shipperNames.includes(initShipperName))

        if (matchedNames.length > 0) {
          setValue('shipperNames', matchedNames)
        }

        isFirstRenderRef.current = false
      }
    }
  }, [isSuccess, setValue])

  return shipperNames
}

/**
 * MEMO:화주선택 필드의 전체 선택 상태 관리 커스텀 훅
 *
 * 정책:
 * : 전체 선택한 필드는 서버에 null 값으로 치환해서 전송하는 정책으로 인해 사용처에서 전체 선택인 상태를 알아야함.
 *
 * case1. 전체 선택을 실제로 선택(onClick)한 경우
 * case2. 전체 선택 및 저장 후 다른 페이지 이동했다가 다시 돌아왔을 경우
 *
 * handleSubmit (검색) 콜백 내에서 전체 선택 필드를 null로 치환하는 로직(processShipperDataForSubmit)이 필요함.

 */
export const useShipperFieldSelection = <T extends Record<string, any>>(initialState: {
  logisticsServiceTypes: boolean
  shipperNames: boolean
}) => {
  const [isAllSelected, setIsAllSelected] = useState(initialState)

  const handleShipperFieldChange = (params: { fieldName: keyof T; value: unknown; isSelectedAll?: boolean }) => {
    // initialState에 포함된 필드만 처리
    if (params.fieldName in initialState && params.isSelectedAll !== undefined) {
      setIsAllSelected((prev) => ({
        ...prev,
        [params.fieldName]: params.isSelectedAll,
      }))
    }
  }

  const resetShipperSelection = () => {
    setIsAllSelected({ logisticsServiceTypes: initialState.logisticsServiceTypes, shipperNames: false })
  }

  // 전체 선택된 경우 null로 치환하는 로직
  const processShipperDataForSubmit = (formData: T) => {
    const processedData = { ...formData }

    // 검색이전에 화주이름, 화주유형이 전체로 선택된 경우 null로 초기화
    if ('shipperNames' in isAllSelected && isAllSelected.shipperNames) {
      processedData['shipperNames' as keyof T] = null as any
    }

    if ('logisticsServiceTypes' in isAllSelected && isAllSelected.logisticsServiceTypes) {
      processedData['logisticsServiceTypes' as keyof T] = null as any
    }

    return processedData
  }

  return {
    isAllSelected,
    setIsAllSelected,
    handleShipperFieldChange,
    resetShipperSelection,
    processShipperDataForSubmit,
  }
}

/**
 * 화주 선택 컴포넌트
 *
 * 주요 정책:
 * 1. "전체 선택" 시 isSelectedAll: true를 onFieldChange로 전달
 * 2. 화주유형 변경 시 화주명을 빈 배열로 리셋
 * 3. 화주유형이 전체 선택된 경우 화주명 전체 선택 불가
 * 4. onlyFullSearchShipperNamesMode가 true인 경우 화주명 전체 선택만 가능
 *
 * 사용처에서 반드시 구현해야 할 사항:
 * 1. onFieldChange 콜백 구현
 * 2. isSelectedAll 상태 별도 관리
 * 3. handleSubmit 콜백 내에서 전체 선택 필드를 null로 치환하는 로직(processShipperDataForSubmit)이 필요함.
 * 4. handleSubmit 콜백 내에서 전체 선택인 경우에 sessionStorage(MenuStorage)내에 isShipperNamesSelectedAll값 true로 초기화 필요
 *
 *
 * 예시:
 * ```tsx
 * const { isAllSelected, handleShipperFieldChange, processShipperDataForSubmit } =
 *   useShipperFieldSelection({ shipperNames: false, logisticsServiceTypes: false })
 *
 * <ShipperSelector onFieldChange={handleShipperFieldChange} />
 *
 * // form submit 시
 * const processedData = processShipperDataForSubmit(formData)
 * await submitToServer(processedData)
 * MenuStorage.menuOnSession.setSearch({ ...formData, isShipperNamesSelectedAll: isAllSelected.shipperNames })
 * ```
 */
function ShipperSelector<T extends FieldValues = GetShipperListV2Req>({
  onFieldChange,
  logisticsServiceTypesWidth = 100,
  shipperNamesWidth = 250,
  disabled = false,
  /**
   * 화주유형을 단일 선택으로 해야하는 경우
   *
   * 예시: 출고대기현황 B2C & B2B화면메뉴에서는 화주유형을 단일 선택만 가능
   * 정책: 화주유형을 단일 선택으로 해야하는 경우
   */
  isSingleLogisticsServiceType = false,
  onlyFullSearchShipperNamesMode = false,
  fieldNames = {
    shipperNames: 'shipperNames' as Path<T>,
    logisticsServiceTypes: 'logisticsServiceTypes' as Path<T>,
  },
}: ShipperSelectorProps<T>) {
  const { watch, control, setValue } = useFormContext<T>()

  // 화주 잠금 판정은 2.0 공용 규칙 — FREE_SHIPPERS 4종 외는 자기 화주로 고정 (LOGISPRD-14650)
  //  재고통합 ON 이면 잠긴 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고른다. FF 응답 전(ready false)엔 범위를 만들지 않는다 (LOGISPRD-15651)
  const { ready, coMingle } = useCoMingleShipperScope()
  const baseScope = useMemo(() => shipperScopeOf(AuthStorage.user.get()), [])
  const scope = useMemo(() => (ready ? withCoMingle(baseScope, coMingle) : baseScope), [ready, coMingle, baseScope])
  const isShipperLocked = scope.locked
  /** 화주명을 고를 수 있는 잠긴 계정(재고통합 대상) — 화주명 칸만 열고 화주유형 칸은 잠근다 */
  const isShipperPickable = isShipperLocked && canSelectShipper(scope)
  const pickableShipperNames = useMemo(() => selectableShippers(scope).map((s) => s.shipperName), [scope])
  const defaultShipperName = defaultShipper(scope).shipperName
  const userShipperName = useMemo(() => AuthStorage.user.get().shipperName ?? '', [])
  const shipperNamesValue: unknown = watch(fieldNames.shipperNames)
  const logisticsServiceTypesWatched: unknown = watch(fieldNames.logisticsServiceTypes)

  useEffect(() => {
    if (!ready || !isShipperLocked || isShipperPickable) return
    setValue(fieldNames.logisticsServiceTypes, [] as unknown as PathValue<T, Path<T>>)

    if (userShipperName) {
      setValue(fieldNames.shipperNames, [userShipperName] as unknown as PathValue<T, Path<T>>)
    }
  }, [ready, isShipperLocked, isShipperPickable, userShipperName, setValue, fieldNames])

  // 화주명을 고를 수 있는 잠긴 계정 — 고를 수 있는 화주면 그대로 두고, 비었거나 옵션 밖 값이면 기본 화주(MUSINSA, LOGISPRD-15718)로 덮는다.
  //  값이 바뀔 때마다 다시 본다 — URL 복원·초기화로 비거나 옵션 밖이 되어도 기본 화주로 돌아온다
  useEffect(() => {
    if (!isShipperPickable) return
    if (!Array.isArray(logisticsServiceTypesWatched) || logisticsServiceTypesWatched.length > 0) {
      setValue(fieldNames.logisticsServiceTypes, [] as unknown as PathValue<T, Path<T>>)
    }
    const kept =
      Array.isArray(shipperNamesValue) &&
      shipperNamesValue.length > 0 &&
      shipperNamesValue.every((name: unknown) => pickableShipperNames.includes(String(name)))
    if (!kept) {
      setValue(fieldNames.shipperNames, [defaultShipperName] as unknown as PathValue<T, Path<T>>)
    }
  }, [isShipperPickable, pickableShipperNames, shipperNamesValue, logisticsServiceTypesWatched, defaultShipperName, setValue, fieldNames])

  const isDisabled = disabled || isShipperLocked
  // 전체 선택만 허용하는 모드(GI23 MFS)는 고를 수 있는 잠긴 계정에 걸지 않는다 — 걸면 복원한 화주가 두 화주 전체로 덮인다
  const isFullSearchShipperNamesMode = onlyFullSearchShipperNamesMode && !isShipperPickable
  const isShipperNamesDisabled = disabled || (isShipperLocked && !isShipperPickable)

  const getMappedOptions = (list: string[]) => {
    return list.map((item) => ({
      key: item,
      value: item,
      label: item,
    }))
  }

  const logisticsServiceTypesValue = watch(fieldNames.logisticsServiceTypes as any)
  const logisticsServiceTypes = Array.isArray(logisticsServiceTypesValue) ? logisticsServiceTypesValue : []

  // 화주명 데이터 가져오기 및 초기값 설정
  const shipperNames = useShipperNamesWithInit({
    logisticsServiceTypes,
    setValue,
    onlyFullSearchShipperNamesMode: isFullSearchShipperNamesMode,
    isShipperLocked,
  })

  // 정책: 화주유형을 전체 선택할 경우 화주명 전체 선택 불가
  const diableShipperNamesSelectAll = useMemo(() => {
    return logisticsServiceTypesValue.length === INIT_FORM.logisticsServiceTypes.length
  }, [logisticsServiceTypesValue])

  return (
    <Container>
      {/* 화주유형 선택 */}
      <Controller
        name={fieldNames.logisticsServiceTypes as any}
        control={control}
        render={({ field }) => {
          if (isSingleLogisticsServiceType) {
            const currentValue = Array.isArray(field.value) && field.value.length > 0 && field.value[0] ? field.value[0] : undefined

            return (
              <StyledSelectBox
                value={currentValue}
                onChange={(value) => {
                  const newValue = value ? [value] : undefined
                  field.onChange(newValue)
                  onFieldChange?.({
                    fieldName: fieldNames.logisticsServiceTypes as keyof T,
                    value: newValue,
                    isSelectedAll: false, // 단일 선택이므로 항상 false
                  })
                  setValue(fieldNames.shipperNames, [] as unknown as PathValue<T, Path<T>>)
                }}
                options={getMappedOptions(INIT_FORM.logisticsServiceTypes)}
                placeholder="화주유형"
                $width={logisticsServiceTypesWidth}
                disabled={isDisabled}
              />
            )
          }

          return (
            <MultipleSelectBox
              size="md"
              options={getMappedOptions(INIT_FORM.logisticsServiceTypes)}
              placeholder="화주유형"
              values={Array.isArray(field.value) ? field.value : []}
              onChange={(value) => {
                field.onChange(value)
                // 정책: 선택된 개수와 전체 개수를 비교하여 isSelectedAll 계산
                onFieldChange?.({
                  fieldName: fieldNames.logisticsServiceTypes as keyof T,
                  value,
                  isSelectedAll: value.length === INIT_FORM.logisticsServiceTypes.length,
                })
                setValue(fieldNames.shipperNames, [] as unknown as PathValue<T, Path<T>>)
              }}
              $width={logisticsServiceTypesWidth}
              disabled={isDisabled}
            />
          )
        }}
      />

      {/* 화주명 선택 */}
      <Controller
        name={fieldNames.shipperNames as any}
        control={control}
        render={({ field }) => {
          const values = isShipperPickable
            ? Array.isArray(field.value)
              ? field.value
              : []
            : isShipperLocked
              ? userShipperName
                ? [userShipperName]
                : []
              : shipperNames
                ? Array.isArray(field.value)
                  ? field.value
                  : []
                : []

          const options = isShipperPickable
            ? getMappedOptions(pickableShipperNames)
            : isShipperLocked
              ? getMappedOptions(userShipperName ? [userShipperName] : [])
              : getMappedOptions(shipperNames || [])

          return (
            <StyledSearchableMultipleSelect
              options={options}
              disableSelectAll={diableShipperNamesSelectAll}
              values={values}
              onChange={(value) => {
                if (isShipperPickable) {
                  field.onChange(value)
                  // 고른 화주명을 그대로 싣는다. 둘 다 골라도 허용 화주 일부라 전체 선택(null 치환)이 아니다
                  onFieldChange?.({
                    fieldName: fieldNames.shipperNames as keyof T,
                    value,
                    isSelectedAll: false,
                  })
                  return
                }
                if (shipperNames) {
                  field.onChange(value)
                  // 정책: 선택된 개수와 전체 개수를 비교하여 isSelectedAll 계산
                  onFieldChange?.({
                    fieldName: fieldNames.shipperNames as keyof T,
                    value: value,
                    isSelectedAll: value.length === shipperNames.length,
                  })
                }
              }}
              onlySelectAllMode={isFullSearchShipperNamesMode}
              $width={shipperNamesWidth}
              disabled={isShipperNamesDisabled}
            />
          )
        }}
      />
    </Container>
  )
}

export default ShipperSelector
